import { useEffect, useId, useMemo, useRef, useState, type Ref } from 'react';
import { Search } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Checkbox } from '@/components/ui/Checkbox';
import { FieldError } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { practitionerTypeaheadRows, type PractitionerPickRow } from '@/lib/practitionerSuggestions';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * Behandlere on forløb Vælg tid and Ny aftale. Same search list as Ny aftale →
 * patient: the matches open under the field. Several people can be checked.
 * Selected people stay pinned above the matches so a long list cannot hide them.
 */
export function PractitionerTypeahead({
  people,
  selectedIds,
  favoriteIds = [],
  onChange,
  legend,
  searchPlaceholder,
  emptyLabel,
  suggestionLabel,
  hint,
  error,
  inputRef,
  disabled = false,
}: {
  people: readonly PractitionerPickRow[];
  selectedIds: readonly string[];
  /** Linked clinicians to show above the rest. Display only — never selected here. */
  favoriteIds?: readonly string[];
  onChange: (ids: string[]) => void;
  legend: string;
  searchPlaceholder: string;
  emptyLabel: string;
  suggestionLabel?: string;
  hint?: string;
  error?: string;
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const favorites = useMemo(() => new Set(favoriteIds), [favoriteIds]);
  const {
    pinned,
    favorites: favoriteRows,
    matches,
  } = useMemo(
    () => practitionerTypeaheadRows({ query, people, selectedIds, favoriteIds }),
    [favoriteIds, people, query, selectedIds],
  );
  const trimmed = query.trim().toLowerCase();
  const pinnedMatchesQuery =
    trimmed.length > 0 && pinned.some((person) => person.name.toLowerCase().includes(trimmed));
  const showEmpty =
    open &&
    trimmed.length > 0 &&
    matches.length === 0 &&
    favoriteRows.length === 0 &&
    !pinnedMatchesQuery;

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function toggle(id: string, checked: boolean) {
    if (disabled) return;
    if (checked) {
      if (!selected.has(id)) onChange([...selectedIds, id]);
    } else {
      onChange(selectedIds.filter((value) => value !== id));
    }
    setQuery('');
    setOpen(false);
  }

  return (
    <fieldset data-testid="practitioner-search-list">
      <legend className={cn(TYPE.small, 'mb-s2 font-medium text-ds-text')}>{legend}</legend>
      <div ref={wrapperRef} className="relative">
        <Input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          aria-expanded={open}
          aria-controls={listId}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          disabled={disabled}
          leadingIcon={<Search className="h-4 w-4" aria-hidden />}
        />
        <div className="min-h-10">
        {!open && pinned.length > 0 ? (
          <ul className="mt-s1" data-testid="practitioner-selected">
            {pinned.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                checked
                favorite={favorites.has(person.id)}
                disabled={disabled}
                onToggle={toggle}
              />
            ))}
          </ul>
        ) : null}
        </div>
        {open &&
        (pinned.length > 0 || favoriteRows.length > 0 || matches.length > 0 || showEmpty) ? (
          <ul
            id={listId}
            data-testid="practitioner-typeahead"
            className="absolute z-10 mt-s1 max-h-60 w-full overflow-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop"
          >
            {pinned.length > 0 ? (
              <li className="sticky top-0 z-[1] border-b border-ds-line bg-ds-card">
                <ul>
                  {pinned.map((person) => (
                    <PersonRow
                      key={person.id}
                      person={person}
                      checked
                      favorite={favorites.has(person.id)}
                      disabled={disabled}
                      onToggle={toggle}
                    />
                  ))}
                </ul>
              </li>
            ) : null}
            {favoriteRows.length > 0 ? (
              <li className="border-b border-ds-line">
                {suggestionLabel ? (
                  <p
                    className={cn(TYPE.meta, 'px-s3 pb-s1 pt-s2 text-ds-muted')}
                    data-testid="practitioner-suggestions"
                  >
                    {suggestionLabel}
                  </p>
                ) : null}
                <ul>
                  {favoriteRows.map((person) => (
                    <PersonRow
                      key={person.id}
                      person={person}
                      checked={false}
                      favorite
                      disabled={disabled}
                      onToggle={toggle}
                    />
                  ))}
                </ul>
              </li>
            ) : null}
            {matches.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                checked={false}
                favorite={false}
                disabled={disabled}
                onToggle={toggle}
              />
            ))}
            {showEmpty ? (
              <li>
                <p
                  className={cn(TYPE.small, 'px-s3 py-s2 text-ds-muted')}
                  data-testid="practitioner-search-empty"
                >
                  {emptyLabel}
                </p>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
      {error ? <FieldError>{error}</FieldError> : null}
      {hint ? <p className={cn('mt-s2', TYPE.small, 'text-ds-muted')}>{hint}</p> : null}
    </fieldset>
  );
}

function PersonRow({
  person,
  checked,
  favorite,
  disabled,
  onToggle,
}: {
  person: PractitionerPickRow;
  checked: boolean;
  favorite: boolean;
  disabled: boolean;
  onToggle: (id: string, checked: boolean) => void;
}) {
  return (
    <li data-favorite={favorite ? 'true' : undefined}>
      <div className={cn('px-s3 py-s2 hover:bg-ds-teal-tint', favorite && 'bg-ds-amber-tint')}>
        <Checkbox
          checked={checked}
          disabled={disabled}
          className="min-h-0 items-center"
          onChange={(event) => onToggle(person.id, event.target.checked)}
          label={
            <span className="flex min-w-0 items-center gap-s3">
              <Avatar name={person.name} size={36} />
              <span className={cn(TYPE.section, 'truncate text-ds-text')} data-practitioner-name>
                {person.name}
              </span>
            </span>
          }
        />
      </div>
    </li>
  );
}
